import { Modal } from "common/Modal"
import { formatDate } from "utils/funcs"
import { getOperationExpenses, OPERATION_DETAIL_FIELDS } from "./utils"

type Props = {
    showModal: boolean
    setShowModal: (value: boolean) => void
    operation: any
    onAddExpense: () => void
    onViewAllExpenses: (operation: any) => void
}

export const OperationDetailsModal = ({
    showModal,
    setShowModal,
    operation,
    onAddExpense,
    onViewAllExpenses,
}: Props) => {
    if (!operation) {
        return (
            <Modal
                showModal={showModal}
                setShowModal={setShowModal}
                title="Operation Details"
                width="w-full md:w-3/4"
            >
                <p className="text-gray-500 text-center py-8">No operation selected</p>
            </Modal>
        )
    }

    const expenses = getOperationExpenses(operation)

    return (
        <Modal
            showModal={showModal}
            setShowModal={setShowModal}
            title="Operation Details"
            width="w-full md:w-3/4"
        >
            <div className="space-y-6 max-h-[80vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {OPERATION_DETAIL_FIELDS.map(({ key, label, format }) => {
                        const raw = operation[key]
                        const value = format ? format(raw) : (raw ?? "N/A")
                        const isMoney = ["monthlyAllocation", "receiptsTotal", "approvedAmountToPay"].includes(key)
                        return (
                            <div key={key} className="flex flex-col gap-1.5">
                                <label className="field-label">{label}</label>
                                <p className={`text-sm text-gray-900 ${isMoney ? "font-semibold text-green-600" : ""}`}>{value}</p>
                            </div>
                        )
                    })}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Expenses</h3>
                        <div className="flex gap-3">
                            <button
                                onClick={onAddExpense}
                                className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
                            >
                                Add Expense
                            </button>
                            <button
                                onClick={() => {
                                    setShowModal(false)
                                    onViewAllExpenses(operation)
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                View All Expenses
                            </button>
                        </div>
                    </div>
                    {expenses.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {expenses.slice(0, 4).map((expense: any, index: number) => (
                                <div key={expense._id || index} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">${parseFloat(expense.amount || 0).toFixed(2)}</p>
                                            {expense.date && (
                                                <p className="text-xs text-gray-500 mt-1">{formatDate(expense.date)}</p>
                                            )}
                                        </div>
                                    </div>
                                    {expense.files && expense.files.length > 0 && (
                                        <p className="text-xs text-gray-500">Attachments: {expense.files.length}</p>
                                    )}
                                </div>
                            ))}
                            {expenses.length > 4 && (
                                <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                                    <p className="page-subtitle">+{expenses.length - 4} more expenses</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No expenses</p>
                    )}
                </div>
            </div>
        </Modal>
    )
}
